/**
 * Hero component - Landing page hero section
 * Split layout: Left content, Right product demo
 */

import {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Container} from '../ui/Container';
import {Button} from '../ui/Button';
import {ROUTES} from '../../config/constants';

export function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger fade-in animation on mount
    setIsLoaded(true);
  }, []);

  return (
    <section className="relative flex items-center justify-center pt-32 pb-16 md:pb-20 bg-background dark:bg-[#1E1E1E] overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      <Container className="relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-start lg:items-center">
          {/* Left Side: Content */}
          <div className={`pt-8 lg:pt-12 space-y-6 transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold text-textPrimary dark:text-[#CCCCCC] leading-tight tracking-tight">
                Meeting Intelligence
                <br />
                <span className="text-gray-600 dark:text-[#858585]">That Works</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585] leading-relaxed max-w-xl">
                Transform every meeting into actionable intelligence with AI-powered transcription, summaries, and insights that help your team make better decisions faster.
              </p>
            </div>

            {/* CTAs */}
            <div className={`flex flex-col sm:flex-row gap-3 pt-2 transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{transitionDelay: isLoaded ? '200ms' : '0ms'}}
            >
              <Link to={ROUTES.SIGN_IN}>
                <Button variant="primary" size="md" className="w-full sm:w-auto bg-[#1E1E1E] dark:bg-[#1E1E1E] text-white hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] focus:ring-[#1E1E1E] dark:focus:ring-[#1E1E1E] hover:shadow-lg hover:shadow-[#1E1E1E]/25 dark:hover:shadow-[#1E1E1E]/25">
                  Start Free
                </Button>
              </Link>
              <Link to={ROUTES.ENTERPRISE}>
                <Button variant="outline" size="md" className="w-full sm:w-auto">
                  Book a Demo
                </Button>
              </Link>
            </div>

            {/* Trust Indicator */}
            <p className="text-sm text-textTertiary dark:text-[#6A6A6A] pt-2">
              Trusted by teams worldwide
            </p>
          </div>

          {/* Right Side: Product Demo */}
          <div className={`transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
          style={{transitionDelay: isLoaded ? '300ms' : '0ms'}}
          >
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
              {/* Browser Chrome */}
              <div className="bg-gray-100 dark:bg-[#252526] border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 bg-white dark:bg-[#1E1E1E] rounded-md px-4 py-1.5 mx-4 border border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-textTertiary dark:text-[#6A6A6A]">app.voxapilot.com</div>
                </div>
              </div>

              {/* Demo Content - Dashboard View */}
              <div className="p-6 lg:p-8 bg-white dark:bg-[#1E1E1E]">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC]">Dashboard</h3>
                    <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                      Live
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      {label: 'Meetings', value: '12'},
                      {label: 'Hours', value: '8.2h'},
                      {label: 'Items', value: '24'},
                    ].map((stat) => (
                      <div key={stat.label} className="bg-gray-50 dark:bg-[#252526] rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                        <div className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC]">{stat.value}</div>
                        <div className="text-xs text-textSecondary dark:text-[#858585]">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Recent Meeting */}
                  <div className="bg-gray-50 dark:bg-[#252526] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-1">Q4 Planning Session</div>
                        <div className="text-xs text-textSecondary dark:text-[#858585]">45 min • 3 participants</div>
                      </div>
                      <div className="text-xs text-textTertiary dark:text-[#6A6A6A]">2h ago</div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="w-6 h-6 bg-[#1E1E1E] dark:bg-[#1E1E1E] rounded-full border-2 border-white dark:border-[#1E1E1E] flex items-center justify-center text-white text-xs font-semibold">
                            {String.fromCharCode(64 + i)}
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-textSecondary dark:text-[#858585]">+ 5 action items</div>
                    </div>
                  </div>

                  {/* Transcript Preview */}
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-textSecondary dark:text-[#858585] uppercase tracking-wide">Recent Activity</div>
                    <div className="space-y-2">
                      {[
                        {speaker: 'Sarah', text: "Let's review the Q4 targets..."},
                        {speaker: 'John', text: "I've prepared the sales forecast..."},
                      ].map((entry, i) => (
                        <div key={i} className="flex gap-2 p-2 bg-gray-50 dark:bg-[#252526] rounded border border-gray-200 dark:border-gray-700">
                          <div className="w-6 h-6 bg-[#1E1E1E] dark:bg-[#1E1E1E] rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                            {entry.speaker[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-textPrimary dark:text-[#CCCCCC]">{entry.speaker}</div>
                            <div className="text-xs text-textSecondary dark:text-[#858585] truncate">{entry.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

